import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, KeyRound, Link2, PlugZap, RefreshCw, ShieldOff } from "lucide-react";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import MaintenanceLayout from "../shared/MaintenanceLayout";
import { useNotice } from "../shared/useNotice";
import {
  AccessRole, LocationRow, Metrics, ocpiApi, OcpiCdrRow, OcpiCommandRow, OcpiPermission, OcpiSessionRow,
  PartnerDetail, RegistrationKey,
} from "../../../api/maintenance/ocpi";
import { ago, canEditOcpi, CopyBtn, errText, fmtTs, groupPermissions, MODULE_LABEL, StatePill } from "./ocpiShared";
import OcpiLogViewer from "./OcpiLogViewer";
import OcpiSubnav from "./OcpiSubnav";
import "../shared/maintenance-analytics.scss";
import "../stations/maintenance-stations.scss";
import "./ocpi.scss";

type Tab = "overview" | "access" | "keys" | "logs" | "activity";

const OcpiPartnerDetail: React.FC = () => {
  const { id } = useParams();
  const partnerId = Number(id);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [tab, setTab] = useState<Tab>(params.get("new") ? "keys" : "overview");
  const [detail, setDetail] = useState<PartnerDetail | null>(null);
  const [roles, setRoles] = useState<AccessRole[]>([]);
  const [catalog, setCatalog] = useState<OcpiPermission[]>([]);
  const [error, setError] = useNotice("error", "");
  const [, setSuccess] = useNotice("success", "");

  const load = useCallback(async () => {
    try {
      const [d, r, c] = await Promise.all([ocpiApi.partner(partnerId), ocpiApi.roles(), ocpiApi.permissions()]);
      setDetail(d.data);
      setRoles(r.data);
      setCatalog(c.data);
    } catch (e: any) {
      setError(errText(e, "Failed to load partner"));
    }
  }, [partnerId, setError]);

  useEffect(() => { load(); }, [load]);

  if (!detail) {
    return <MaintenanceLayout title="OCPI partner"><div className="ma"><OcpiSubnav />{error ? <div className="ma-error">{error}</div> : <p className="ma-loading">Loading…</p>}</div></MaintenanceLayout>;
  }
  const p = detail.partner;
  return (
    <MaintenanceLayout title="OCPI partner">
      <div className="ma">
        <OcpiSubnav />
        <div className="ocpi-header">
          <button className="maint-btn-link" onClick={() => navigate("/maintenance/ocpi")}><ArrowLeft size={14} /> Partners</button>
          <h2>{p.name}</h2>
          <StatePill state={p.registration_state} suspended={p.is_suspended} />
          <span className="ma-pill info">{detail.role.name}</span>
          <span className="ocpi-meta">last seen {ago(p.last_seen_at)}</span>
        </div>
        {p.is_suspended && <div className="ma-error">Suspended: {p.suspended_reason}. Every request from this partner gets HTTP 403.</div>}
        <div className="ma-tabs" role="tablist">
          {([["overview", "Overview"], ["access", "Access & limits"], ["keys", "Keys & connection"], ["logs", "Request log"], ["activity", "Sessions & commands"]] as [Tab, string][]).map(([k, label]) => (
            <button key={k} role="tab" aria-selected={tab === k} className={`ma-tab ${tab === k ? "on" : ""}`} onClick={() => setTab(k)}>{label}</button>
          ))}
        </div>
        {tab === "overview" && <OverviewTab detail={detail} />}
        {tab === "access" && <AccessTab detail={detail} roles={roles} catalog={catalog} reload={load} notify={setSuccess} fail={setError} />}
        {tab === "keys" && <KeysTab detail={detail} reload={load} notify={setSuccess} fail={setError} />}
        {tab === "logs" && <OcpiLogViewer partnerId={partnerId} />}
        {tab === "activity" && <ActivityTab partnerId={partnerId} />}
      </div>
    </MaintenanceLayout>
  );
};

// ------------------------------------------------------------------------------------------------ overview
const OverviewTab: React.FC<{ detail: PartnerDetail }> = ({ detail }) => {
  const [m, setM] = useState<Metrics | null>(null);
  const [hours, setHours] = useState(24);
  useEffect(() => {
    const end = new Date();
    ocpiApi.metrics(detail.partner.id, new Date(end.getTime() - hours * 3600e3).toISOString(), end.toISOString())
      .then((r) => setM(r.data)).catch(() => setM(null));
  }, [detail.partner.id, hours]);
  const series = useMemo(() => (m?.series || []).map((s) => ({
    t: new Date(s.bucket).toLocaleString([], hours > 48 ? { month: "short", day: "numeric" } : { hour: "2-digit", minute: "2-digit" }),
    ok: s.ok, client: s.client_errors, server: s.server_errors, limited: s.rate_limited,
  })), [m, hours]);
  const s = m?.summary;
  const p = detail.partner;
  return (
    <>
      <div className="ma-toolbar">
        <div className="ma-presets">{[1, 24, 168, 720].map((h) => (
          <button key={h} className={`ma-preset ${hours === h ? "on" : ""}`} onClick={() => setHours(h)}>{h === 1 ? "1h" : h === 24 ? "24h" : h === 168 ? "7d" : "30d"}</button>
        ))}</div>
      </div>
      <div className="ma-kpis">
        <div className="ma-kpi"><div className="ma-kpi-label">Requests</div><div className="ma-kpi-value">{s ? Number(s.total).toLocaleString() : "—"}</div><div className="ma-kpi-sub">inbound, this window</div></div>
        <div className="ma-kpi"><div className="ma-kpi-label">Error rate</div><div className="ma-kpi-value">{s && s.total ? `${((100 * s.errors) / s.total).toFixed(1)}%` : "—"}</div><div className="ma-kpi-sub">{s ? `${s.auth_failures} auth failures` : ""}</div></div>
        <div className="ma-kpi"><div className="ma-kpi-label">Rate limited</div><div className="ma-kpi-value">{s ? s.rate_limited : "—"}</div><div className="ma-kpi-sub">HTTP 429 answered</div></div>
        <div className="ma-kpi"><div className="ma-kpi-label">Latency p50 / p95</div><div className="ma-kpi-value">{s?.p50_ms != null ? `${Math.round(s.p50_ms)} / ${Math.round(s.p95_ms || 0)}` : "—"}</div><div className="ma-kpi-sub">milliseconds</div></div>
        <div className="ma-kpi"><div className="ma-kpi-label">Outbound calls</div><div className="ma-kpi-value">{m ? m.outbound.total : "—"}</div><div className="ma-kpi-sub">{m ? `${m.outbound.failed} failed` : ""}</div></div>
      </div>
      <div className="ma-grid">
        <div className="ma-card two-third">
          <div className="ma-card-h"><h3>Traffic</h3><span className="ma-hint">stacked by outcome</span></div>
          <div className="ma-card-b" style={{ height: 260 }}>
            {series.length === 0 ? <p className="ma-empty">No requests in this window.</p> : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={series}>
                  <CartesianGrid stroke="#eef0f4" vertical={false} />
                  <XAxis dataKey="t" tick={{ fontSize: 11, fill: "#8a92a1" }} minTickGap={24} />
                  <YAxis tick={{ fontSize: 11, fill: "#8a92a1" }} allowDecimals={false} width={40} />
                  <Tooltip />
                  <Area type="monotone" dataKey="ok" stackId="1" stroke="#2e7d32" fill="#c8e6c9" name="OK" />
                  <Area type="monotone" dataKey="client" stackId="1" stroke="#e65100" fill="#ffe0b2" name="Client errors" />
                  <Area type="monotone" dataKey="server" stackId="1" stroke="#c62828" fill="#ffcdd2" name="Server errors" />
                  <Area type="monotone" dataKey="limited" stackId="1" stroke="#5554d3" fill="#d9d9fb" name="Rate limited" />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
        <div className="ma-card third">
          <div className="ma-card-h"><h3>Connection</h3></div>
          <div className="ma-card-b">
            <dl className="ocpi-kv">
              <dt>Access role</dt><dd>{detail.role.name}</dd>
              <dt>OCPI parties</dt><dd>{detail.parties.length ? detail.parties.map((x) => `${x.role} ${x.country_code}*${x.party_id}`).join(", ") : "—"}</dd>
              <dt>OCPI version</dt><dd>{p.ocpi_version || "—"}</dd>
              <dt>Registered</dt><dd>{fmtTs(p.registered_at)}</dd>
              <dt>Stations shared</dt><dd>{p.location_scope === "ALL" ? "All public" : p.location_scope === "NONE" ? "None" : `${detail.stations.length} selected`}</dd>
              <dt>CMS account</dt><dd>{detail.cms_identity ? `${detail.cms_identity.org_name} (org ${detail.cms_identity.org_id})` : "not needed yet"}</dd>
              <dt>Contact</dt><dd>{[p.contact_name, p.contact_email].filter(Boolean).join(" · ") || "—"}</dd>
            </dl>
          </div>
        </div>
        <div className="ma-card half">
          <div className="ma-card-h"><h3>By module</h3></div>
          <div className="ma-tablewrap">
            <table className="ma-table">
              <thead><tr><th>Module</th><th className="num">Requests</th><th className="num">Errors</th><th className="num">Avg ms</th></tr></thead>
              <tbody>
                {(m?.by_module || []).map((b) => (
                  <tr key={b.module}><td>{b.module}</td><td className="num">{b.total}</td><td className="num">{b.errors}</td><td className="num">{b.avg_ms ?? "—"}</td></tr>
                ))}
                {m && m.by_module.length === 0 && <tr><td colSpan={4} className="ma-empty">No traffic.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
        <div className="ma-card half">
          <div className="ma-card-h"><h3>History</h3></div>
          <div className="ma-tablewrap">
            <table className="ma-table">
              <tbody>
                {detail.events.map((e) => (
                  <tr key={e.id}><td title={e.at}>{fmtTs(e.at)}</td><td>{e.event.replace(/_/g, " ").toLowerCase()}</td><td className="sub">{e.actor || ""}</td></tr>
                ))}
                {detail.events.length === 0 && <tr><td className="ma-empty">Nothing yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
};

// ------------------------------------------------------------------------------------------------ access
type Notify = (m: string) => void;

const AccessTab: React.FC<{ detail: PartnerDetail; roles: AccessRole[]; catalog: OcpiPermission[]; reload: () => void; notify: Notify; fail: Notify }> = ({
  detail, roles, catalog, reload, notify, fail,
}) => {
  const p = detail.partner;
  const editable = canEditOcpi();
  const [roleId, setRoleId] = useState(p.access_role_id);
  const [overrides, setOverrides] = useState<Record<string, "ALLOW" | "DENY">>(
    Object.fromEntries(detail.overrides.map((o) => [o.permission, o.effect])));
  const [limits, setLimits] = useState({
    requests_per_minute: p.requests_per_minute ?? "", burst: p.burst ?? "", commands_per_minute: p.commands_per_minute ?? "",
    max_page_size: p.max_page_size ?? "",
  } as Record<string, number | "">);
  const [logBodies, setLogBodies] = useState<"" | "on" | "off">(p.log_bodies === null ? "" : p.log_bodies ? "on" : "off");
  const [ips, setIps] = useState(p.allowed_ips.join("\n"));
  const [scope, setScope] = useState(p.location_scope);
  const [stations, setStations] = useState<number[]>(detail.stations);
  const [locations, setLocations] = useState<LocationRow[]>([]);
  const [busy, setBusy] = useState(false);
  const role = roles.find((r) => r.id === roleId) || detail.role;
  const rolePerms = new Set(role.permissions);

  useEffect(() => {
    if (scope === "SELECTED" && locations.length === 0) {
      ocpiApi.locations().then((r) => setLocations(r.data.filter((l) => l.station_type === "public"))).catch(() => {});
    }
  }, [scope, locations.length]);

  const effective = (perm: string) => (overrides[perm] ? overrides[perm] === "ALLOW" : rolePerms.has(perm));

  const save = async () => {
    setBusy(true);
    try {
      const num = (v: number | "") => (v === "" ? null : Number(v));
      await ocpiApi.updatePartner(p.id, {
        access_role_id: roleId, requests_per_minute: num(limits.requests_per_minute), burst: num(limits.burst),
        commands_per_minute: num(limits.commands_per_minute), max_page_size: num(limits.max_page_size),
        log_bodies: logBodies === "" ? null : logBodies === "on", location_scope: scope,
        allowed_ips: ips.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean),
      } as any);
      await ocpiApi.setOverrides(p.id, Object.entries(overrides).map(([permission, effect]) => ({ permission, effect })));
      if (scope === "SELECTED") await ocpiApi.setStations(p.id, stations);
      notify("Access updated — takes effect within seconds");
      reload();
    } catch (e: any) {
      fail(errText(e, "Failed to save access settings"));
    } finally {
      setBusy(false);
    }
  };

  const suspendToggle = async () => {
    try {
      if (p.is_suspended) {
        await ocpiApi.resume(p.id);
        notify("Partner resumed");
      } else {
        const reason = window.prompt("Reason for suspending this partner (shown in the history):");
        if (!reason || reason.trim().length < 3) return;
        await ocpiApi.suspend(p.id, reason.trim());
        notify("Partner suspended — all its requests now get HTTP 403");
      }
      reload();
    } catch (e: any) {
      fail(errText(e, "Failed to change suspension"));
    }
  };

  const limitRow = (key: string, label: string) => (
    <label key={key}>{label}
      <input type="number" min={key === "commands_per_minute" ? 0 : 1} disabled={!editable} value={limits[key]}
             placeholder={`role default: ${(role as any)[key]}`}
             onChange={(e) => setLimits((l) => ({ ...l, [key]: e.target.value === "" ? "" : Number(e.target.value) }))} />
    </label>
  );

  return (
    <div className="ma-grid">
      <div className="ma-card">
        <div className="ma-card-h"><h3>Access role</h3><span className="ma-hint">permissions + default limits come from the role</span></div>
        <div className="ma-card-b">
          <div className="ocpi-form-grid">
            <label>Role
              <select value={roleId} disabled={!editable} onChange={(e) => setRoleId(Number(e.target.value))}>
                {roles.filter((r) => !r.is_archived || r.id === p.access_role_id).map((r) => (
                  <option key={r.id} value={r.id}>{r.name}{r.is_system ? "" : " (custom)"}</option>
                ))}
              </select>
            </label>
          </div>
          {role.description && <div className="ma-note" style={{ marginTop: 10 }}>{role.description}</div>}
          <div className="ocpi-perms" style={{ marginTop: 10 }}>
            {groupPermissions(catalog).map((g) => (
              <div className="ocpi-perm-group" key={g.module}>
                <h4>{MODULE_LABEL[g.module] || g.module}</h4>
                {g.items.map((perm) => {
                  const ov = overrides[perm.permission];
                  return (
                    <div key={perm.permission} className={`ocpi-perm ${perm.implemented ? "" : "disabled"}`}>
                      <span aria-hidden>{effective(perm.permission) ? "✅" : "⛔"}</span>
                      <div>
                        <div>{perm.label}{!perm.implemented && <span className="ma-pill muted" style={{ marginLeft: 6 }}>not yet supported</span>}</div>
                        <div className="ocpi-perm-desc">{rolePerms.has(perm.permission) ? "granted by role" : "not in role"}</div>
                      </div>
                      <div className="ocpi-override" role="group" aria-label={`Override ${perm.label}`}>
                        {(["", "ALLOW", "DENY"] as const).map((eff) => (
                          <button key={eff || "inherit"} type="button" disabled={!editable || !perm.implemented}
                                  className={`${eff === "ALLOW" ? "allow" : eff === "DENY" ? "deny" : ""} ${(ov || "") === eff ? "on" : ""}`}
                                  onClick={() => setOverrides((o) => { const n = { ...o }; if (eff) n[perm.permission] = eff; else delete n[perm.permission]; return n; })}>
                            {eff === "" ? "Role" : eff === "ALLOW" ? "Allow" : "Deny"}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="ma-card half">
        <div className="ma-card-h"><h3>Rate limits</h3><span className="ma-hint">blank = role default</span></div>
        <div className="ma-card-b">
          <div className="ocpi-form-grid">
            {limitRow("requests_per_minute", "Requests / minute")}
            {limitRow("burst", "Burst")}
            {limitRow("commands_per_minute", "Commands / minute")}
            {limitRow("max_page_size", "Max page size")}
            <label>Log request/response bodies
              <select value={logBodies} disabled={!editable} onChange={(e) => setLogBodies(e.target.value as any)}>
                <option value="">Role default ({role.log_bodies ? "on" : "off"})</option><option value="on">On</option><option value="off">Off</option>
              </select>
            </label>
          </div>
          <p className="ma-hint" style={{ marginTop: 8 }}>Over the limit the partner gets HTTP 429 with Retry-After. Credentials tokens are never logged, bodies or not.</p>
        </div>
      </div>

      <div className="ma-card half">
        <div className="ma-card-h"><h3>Network & data scope</h3></div>
        <div className="ma-card-b">
          <div className="ocpi-form-grid">
            <label>Allowed source IPs / CIDRs (blank = any)
              <textarea disabled={!editable} value={ips} onChange={(e) => setIps(e.target.value)} placeholder={"203.0.113.0/24\n198.51.100.7"} />
            </label>
            <label>Stations shared
              <select value={scope} disabled={!editable} onChange={(e) => setScope(e.target.value as any)}>
                <option value="ALL">All public stations</option><option value="SELECTED">Only selected stations</option><option value="NONE">None</option>
              </select>
            </label>
          </div>
          {scope === "SELECTED" && (
            <div style={{ maxHeight: 220, overflowY: "auto", marginTop: 10, border: "1px solid #e6e8ee", borderRadius: 8, padding: 8 }}>
              {locations.map((l) => (
                <label key={l.charging_station_id} style={{ display: "flex", gap: 8, fontSize: 13, padding: "3px 0" }}>
                  <input type="checkbox" disabled={!editable} checked={stations.includes(l.charging_station_id)}
                         onChange={(e) => setStations((s) => e.target.checked ? [...s, l.charging_station_id] : s.filter((x) => x !== l.charging_station_id))} />
                  {l.station_name}
                </label>
              ))}
              {locations.length === 0 && <p className="ma-hint">Loading stations…</p>}
            </div>
          )}
        </div>
      </div>

      {editable && (
        <div className="ma-card">
          <div className="ma-card-b" style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
            <button className="ma-btn primary" onClick={save} disabled={busy}>{busy ? "Saving…" : "Save access & limits"}</button>
            <span className="ma-spacer" />
            <button className={`ma-btn ${p.is_suspended ? "primary" : ""}`} onClick={suspendToggle}>
              <ShieldOff size={14} /> {p.is_suspended ? "Resume partner" : "Suspend partner"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ------------------------------------------------------------------------------------------------ keys
const KeysTab: React.FC<{ detail: PartnerDetail; reload: () => void; notify: Notify; fail: Notify }> = ({ detail, reload, notify, fail }) => {
  const p = detail.partner;
  const editable = canEditOcpi();
  const [issued, setIssued] = useState<RegistrationKey | null>(null);
  const [busy, setBusy] = useState(false);
  const [theirUrl, setTheirUrl] = useState("");
  const [theirToken, setTheirToken] = useState("");
  const [jobId, setJobId] = useState<number | null>(null);
  const [jobState, setJobState] = useState<string>("");

  useEffect(() => {
    if (!jobId) return;
    const t = setInterval(async () => {
      try {
        const j = (await ocpiApi.job(p.id, jobId)).data;
        setJobState(j.state + (j.error ? `: ${j.error}` : ""));
        if (j.state === "SUCCEEDED" || j.state === "FAILED") {
          clearInterval(t);
          setJobId(null);
          if (j.state === "SUCCEEDED") notify("Done"); else fail(j.error || "Job failed");
          reload();
        }
      } catch { /* keep polling */ }
    }, 2000);
    return () => clearInterval(t);
  }, [jobId, p.id, notify, fail, reload]);

  const issue = async () => {
    if (p.registration_state === "PENDING" && !window.confirm("An unused registration key already exists; issuing a new one invalidates it. Continue?")) return;
    setBusy(true);
    try {
      setIssued((await ocpiApi.issueRegistrationKey(p.id)).data);
      reload();
    } catch (e: any) {
      fail(errText(e, "Failed to issue key"));
    } finally {
      setBusy(false);
    }
  };

  const job = async (type: string, confirmText?: string) => {
    if (confirmText && !window.confirm(confirmText)) return;
    try {
      const r = await ocpiApi.createJob(p.id, type, type === "REGISTER_WITH_PARTNER" ? theirUrl : undefined,
                                        type === "REGISTER_WITH_PARTNER" ? theirToken : undefined);
      setTheirToken("");
      setJobId(r.data.job_id);
      setJobState("QUEUED");
    } catch (e: any) {
      fail(errText(e, "Failed to start"));
    }
  };

  const revoke = async () => {
    if (!window.confirm("Revoke ALL keys of this partner now? Every request will fail with 401 until it re-registers.")) return;
    try {
      await ocpiApi.revoke(p.id, "revoked by operator");
      notify("All keys revoked");
      reload();
    } catch (e: any) {
      fail(errText(e, "Failed to revoke"));
    }
  };

  const provision = async () => {
    try {
      await ocpiApi.provisionCms(p.id);
      notify("CMS account ready");
      reload();
    } catch (e: any) {
      fail(errText(e, "Failed to provision"));
    }
  };

  return (
    <div className="ma-grid">
      <div className="ma-card half">
        <div className="ma-card-h"><h3><KeyRound size={15} /> Partner connects to us</h3></div>
        <div className="ma-card-b">
          <p className="ma-hint" style={{ marginTop: 0 }}>
            Give the partner our versions URL and a registration key (securely, e.g. by phone or a password manager share).
            They register with it once; OCPI then switches them to a working key automatically.
          </p>
          <dl className="ocpi-kv">
            <dt>Our versions URL</dt><dd>{detail.versions_url} <CopyBtn value={detail.versions_url} /></dd>
            <dt>Status</dt><dd><StatePill state={p.registration_state} suspended={p.is_suspended} /></dd>
          </dl>
          {issued && (
            <div className="ocpi-secret" role="status">
              <div className="ocpi-warn">Copy this registration key now — it will not be shown again.</div>
              <div className="ocpi-secret-row"><code>{issued.token}</code><CopyBtn value={issued.token} label="Copy key" /></div>
              <div className="ma-hint" style={{ marginTop: 6 }}>Valid until {fmtTs(issued.expires_at)} · single use · key id …{issued.token_hint}</div>
            </div>
          )}
          {editable && p.registration_state !== "REGISTERED" && (
            <button className="ma-btn primary" onClick={issue} disabled={busy} style={{ marginTop: 10 }}>
              <KeyRound size={14} /> {p.registration_state === "PENDING" ? "Re-issue registration key" : "Issue registration key"}
            </button>
          )}
        </div>
      </div>

      <div className="ma-card half">
        <div className="ma-card-h"><h3><Link2 size={15} /> We connect to the partner</h3></div>
        <div className="ma-card-b">
          <p className="ma-hint" style={{ marginTop: 0 }}>Use this when the partner (typically a hub) gave <em>us</em> their versions URL and a token.</p>
          {editable && p.registration_state !== "REGISTERED" && (
            <div className="ocpi-form-grid">
              <label>Partner versions URL<input value={theirUrl} onChange={(e) => setTheirUrl(e.target.value)} placeholder="https://partner.example/ocpi/versions" /></label>
              <label>Partner token<input type="password" autoComplete="off" value={theirToken} onChange={(e) => setTheirToken(e.target.value)} /></label>
            </div>
          )}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
            {editable && p.registration_state !== "REGISTERED" && (
              <button className="ma-btn primary" disabled={!theirUrl || !theirToken || !!jobId} onClick={() => job("REGISTER_WITH_PARTNER")}>
                <PlugZap size={14} /> Register with partner
              </button>
            )}
            {editable && p.registration_state === "REGISTERED" && (
              <>
                <button className="ma-btn" disabled={!!jobId} onClick={() => job("UPDATE_CREDENTIALS", "Rotate keys with this partner now?")}><RefreshCw size={14} /> Rotate keys</button>
                <button className="ma-btn" disabled={!!jobId} onClick={() => job("REFRESH_ENDPOINTS")}>Refresh partner endpoints</button>
              </>
            )}
          </div>
          {jobState && <div className="ma-note" style={{ marginTop: 10 }}>Job: {jobState}</div>}
        </div>
      </div>

      <div className="ma-card">
        <div className="ma-card-h"><h3>Keys we issued</h3><span className="ma-hint">only hashes are stored; the id is the last characters of the hash</span></div>
        <div className="ma-tablewrap">
          <table className="ma-table">
            <thead><tr><th>Key</th><th>Kind</th><th>Status</th><th>Created</th><th>Last used</th><th>Expires</th><th>Revoked</th></tr></thead>
            <tbody>
              {detail.tokens.map((t) => (
                <tr key={t.id}>
                  <td>…{t.token_hint}</td>
                  <td>{t.kind === "A" ? "registration" : "working"}</td>
                  <td><span className={`ma-pill ${t.status === "ACTIVE" ? "ok" : t.status === "USED" ? "muted" : "bad"}`}>{t.status.toLowerCase()}</span></td>
                  <td>{fmtTs(t.created_at)}<div className="sub">{t.created_by}</div></td>
                  <td>{ago(t.last_used_at)}</td>
                  <td>{fmtTs(t.expires_at)}</td>
                  <td>{t.revoked_at ? <>{fmtTs(t.revoked_at)}<div className="sub">{t.revoke_reason}</div></> : "—"}</td>
                </tr>
              ))}
              {detail.tokens.length === 0 && <tr><td colSpan={7} className="ma-empty">No keys issued yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div className="ma-card half">
        <div className="ma-card-h"><h3>Partner endpoints</h3><span className="ma-hint">learned in the handshake</span></div>
        <div className="ma-tablewrap">
          <table className="ma-table">
            <thead><tr><th>Module</th><th>Role</th><th>URL</th></tr></thead>
            <tbody>
              {detail.endpoints.map((e) => (
                <tr key={`${e.module}-${e.interface_role}`}><td>{e.module}</td><td>{e.interface_role}</td><td className="sub" title={e.url}>{e.url}</td></tr>
              ))}
              {detail.endpoints.length === 0 && <tr><td colSpan={3} className="ma-empty">Not registered.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div className="ma-card half">
        <div className="ma-card-h"><h3>Charging account</h3></div>
        <div className="ma-card-b">
          <p className="ma-hint" style={{ marginTop: 0 }}>
            Roaming sessions run under a dedicated corporate-style org for this partner (never the retail public org),
            created automatically on its first remote start. Set negotiated roaming prices for that org in Pricing.
          </p>
          {detail.cms_identity
            ? <dl className="ocpi-kv"><dt>Org</dt><dd>{detail.cms_identity.org_name} (id {detail.cms_identity.org_id})</dd><dt>Service user</dt><dd>{detail.cms_identity.user_id ?? "—"}</dd></dl>
            : editable && <button className="ma-btn" onClick={provision}>Create charging account now</button>}
        </div>
      </div>

      {editable && (
        <div className="ma-card">
          <div className="ma-card-b">
            <div className="ocpi-danger-zone">
              <h4>Danger zone</h4>
              <p>Revoking keys cuts the partner off immediately (401). Unregistering also tells the partner (OCPI DELETE credentials) and removes its endpoints.</p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                <button className="maint-btn-danger" onClick={revoke}>Revoke all keys</button>
                {p.registration_state === "REGISTERED" && (
                  <button className="maint-btn-danger" disabled={!!jobId} onClick={() => job("UNREGISTER", "Unregister this partner? They will have to register again to reconnect.")}>Unregister partner</button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ------------------------------------------------------------------------------------------------ activity
const ActivityTab: React.FC<{ partnerId: number }> = ({ partnerId }) => {
  const [sessions, setSessions] = useState<OcpiSessionRow[]>([]);
  const [cdrs, setCdrs] = useState<OcpiCdrRow[]>([]);
  const [commands, setCommands] = useState<OcpiCommandRow[]>([]);
  useEffect(() => {
    ocpiApi.sessions(partnerId).then((r) => setSessions(r.data)).catch(() => {});
    ocpiApi.cdrs(partnerId).then((r) => setCdrs(r.data)).catch(() => {});
    ocpiApi.commands(partnerId).then((r) => setCommands(r.data)).catch(() => {});
  }, [partnerId]);
  const money = (v: string | null) => (v == null ? "—" : `₹${Number(v).toFixed(2)}`);
  return (
    <div className="ma-grid">
      <div className="ma-card">
        <div className="ma-card-h"><h3>Remote commands</h3></div>
        <div className="ma-tablewrap">
          <table className="ma-table">
            <thead><tr><th>Time</th><th>Command</th><th>Answer</th><th>Charger result</th><th>Delivered to partner</th><th>Target</th></tr></thead>
            <tbody>
              {commands.map((c) => (
                <tr key={c.id}>
                  <td>{fmtTs(c.created_at)}</td><td>{c.command}</td>
                  <td>{c.response_result}{c.response_message && <div className="sub">{c.response_message}</div>}</td>
                  <td>{c.result || (c.state === "RUNNING" ? "waiting…" : "—")}{c.result_message && <div className="sub">{c.result_message}</div>}</td>
                  <td>{c.delivered_at ? fmtTs(c.delivered_at) : c.state === "DELIVERY_FAILED" ? <span className="ma-pill bad" title={c.last_delivery_error || ""}>failed</span> : "—"}</td>
                  <td className="sub">{c.session_id || [c.location_id, c.evse_uid].filter(Boolean).join(" / ") || "—"}</td>
                </tr>
              ))}
              {commands.length === 0 && <tr><td colSpan={6} className="ma-empty">No commands yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      <div className="ma-card half">
        <div className="ma-card-h"><h3>Roaming sessions</h3></div>
        <div className="ma-tablewrap">
          <table className="ma-table">
            <thead><tr><th>Started</th><th>Status</th><th className="num">kWh</th><th className="num">Total incl. GST</th><th>CMS session</th></tr></thead>
            <tbody>
              {sessions.map((s) => (
                <tr key={s.id}><td>{fmtTs(s.start_date_time)}</td><td>{s.status}</td><td className="num">{Number(s.kwh).toFixed(3)}</td>
                  <td className="num">{money(s.total_cost_incl)}</td><td className="sub">#{s.charging_session_id}</td></tr>
              ))}
              {sessions.length === 0 && <tr><td colSpan={5} className="ma-empty">No sessions yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      <div className="ma-card half">
        <div className="ma-card-h"><h3>CDRs</h3></div>
        <div className="ma-tablewrap">
          <table className="ma-table">
            <thead><tr><th>Ended</th><th className="num">kWh</th><th className="num">Excl. GST</th><th className="num">Incl. GST</th></tr></thead>
            <tbody>
              {cdrs.map((c) => (
                <tr key={c.id}><td>{fmtTs(c.end_date_time)}</td><td className="num">{Number(c.total_energy).toFixed(3)}</td>
                  <td className="num">{money(c.total_cost_excl)}</td><td className="num">{money(c.total_cost_incl)}</td></tr>
              ))}
              {cdrs.length === 0 && <tr><td colSpan={4} className="ma-empty">No CDRs yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default OcpiPartnerDetail;
